import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { merge } from 'rxjs';

import { Menu } from 'app/shared/models/control/menu';
import { PagesMenu } from 'app/pages/pages.menu';
import { MenuNavigationService } from 'app/shared/services/menu-navigation/menu-navigation.service';
import { MenuKey } from 'app/shared/services/menu-open-screens/open-screens.service';
import { MenuPermissionService } from 'app/shared/services/menu-permissions/menu-permission.service';
import { normalizeAppUrl } from 'app/shared/utils/app-url.util';

export interface FlatMenuEntry {
    id: number;
    /**
     * The node's chain from its rail module down - the same identity the sidebar tree and
     * OpenScreensService use. Bare ids collide across PagesMenu (see MenuKey), so this is what
     * rows are tracked by and matched against open screens by.
     */
    key: MenuKey;
    title: string;
    path: string;
    moduleId: number;
    icon?: string;
    item: Menu;
}

interface FlatMenuRaw {
    id: number;
    key: MenuKey;
    titleKey: string;
    ancestorTitleKeys: string[];
    /**
     * The `path` of every node above this one, outermost first - what
     * MenuPermissionService.isVisible() needs to apply the sidebar's cascade, where a screen the
     * user may open still disappears along with a section they may not.
     */
    ancestorPaths: string[];
    moduleId: number;
    icon?: string;
    item: Menu;
}

/** Cap on the memoized row lists - see memoized(). */
const ROWS_MEMO_CAP = 32;

/**
 * Flattens the static PagesMenu tree once at construction so the shell's search box can do a
 * plain substring match, mirroring the design mockup's `this.flat`/`searchRows()`. The one part
 * of the menu that is not static is folded in afterwards by registerRuntimeModule().
 *
 * The index holds every screen in the menu; what the user may actually reach is decided per row,
 * when rows are handed out, by MenuPermissionService - the same path->permission rule the sidebar
 * and AuthGuard apply. It cannot be baked into the index: PagesMenu is walked once at
 * construction, and the shell is reachable before the user's permissions have arrived
 * (see AuthorizeService.permissionsChanged), so a verdict computed here would be a verdict about
 * an empty permission list.
 *
 * Also the lookup side of the flyout's Pinned list: a favorite record stores only a Path and comes
 * back here for the title, breadcrumb and module icon a row needs, so the list cannot drift from
 * what search and the tree show.
 *
 * Translation keys (not translated strings) are cached at construction, and resolved via
 * translate.instant() on every search call instead - caching translated text once would freeze
 * search results in whichever language happens to be loaded first and never update on a language
 * switch (ngx-translate's translations load asynchronously, so translate.instant() at
 * construction time may run before they're ready anyway).
 *
 * "Leaf" detection reuses MenuNavigationService.isNavigable() rather than a separate
 * reimplementation, so a `lastChild:true` node that *also* has children (e.g. "System
 * Parameter", which still navigates via the HomeItems flow and hands its children to Home's own
 * grid) is correctly treated as independently searchable/navigable, same as the rail/flyout tree.
 */
@Injectable({ providedIn: 'root' })
export class MenuSearchService {

    private readonly flat: FlatMenuRaw[];
    private readonly rowsMemo = new Map<string, FlatMenuEntry[]>();
    private memoLang: string | undefined;

    constructor(private readonly translate: TranslateService, private readonly navigation: MenuNavigationService,
        private readonly permissions: MenuPermissionService) {
        this.flat = [];
        this.walk(PagesMenu, [], [], 0, undefined, '');
        // These are the three events TranslatePipe itself re-renders on, so a title resolved here
        // and the same title rendered through the pipe cannot drift apart. The currentLang check in
        // memoized() is not enough on its own: ngx-translate fetches each language file over HTTP,
        // so the first rows are built before any translation exists and, with currentLang never
        // changing afterwards, nothing would invalidate them - leaving the flyout's Pinned list on
        // English screen names for the whole session while the rail, the module tree and the Home
        // pinned strip show those same screens in Arabic. Never unsubscribed on purpose: the
        // service is providedIn:'root' and lives exactly as long as the app does.
        // permissions.changed is in here for the same reason, one step further on: rows are
        // filtered by what the user may open, and the flyout can be searched before the
        // permissions call has answered - a row list memoized in that window was filtered
        // against nothing and would otherwise stand for the rest of the session.
        merge(translate.onLangChange, translate.onDefaultLangChange, translate.onTranslationChange,
            this.permissions.changed)
            .subscribe(() => this.rowsMemo.clear());
    }

    private walk(nodes: Menu[], ancestorTitleKeys: string[], ancestorPaths: string[], moduleId: number,
        moduleIcon: string | undefined, parentKey: MenuKey): void {
        for (const node of nodes) {
            if (node.isVisible === false) {
                continue;
            }
            const key = parentKey ? `${parentKey}/${node.id}` : `${node.id}`;
            const currentModuleId = node.isMain ? node.id : moduleId;
            const currentModuleIcon = node.isMain ? node.icon : moduleIcon;
            if (!node.isMain && this.navigation.isNavigable(node)) {
                const raw: FlatMenuRaw = {
                    id: node.id,
                    key,
                    titleKey: node.title,
                    ancestorTitleKeys,
                    ancestorPaths,
                    moduleId: currentModuleId,
                    icon: currentModuleIcon,
                    item: node,
                };
                this.flat.push(raw);
            }
            if (node.children?.length) {
                this.walk(node.children, [...ancestorTitleKeys, node.title], [...ancestorPaths, node.path],
                    currentModuleId, currentModuleIcon, key);
            }
        }
    }

    /**
     * Folds a module that is not part of PagesMenu into the index - the rail's Tools module,
     * whose shared dynamic modules are per-user and arrive over HTTP long after this service is
     * built (see SharedModulesMenuService). Without this they would be the one part of the menu
     * the search box cannot find, and a pin saved for one would drop out of the flyout's Pinned
     * list, which resolves its rows through this index.
     *
     * Re-registering the same module replaces its entries rather than duplicating them, and the
     * memo is dropped either way: rows built before the module landed do not know about it.
     */
    registerRuntimeModule(module: Menu): void {
        for (let i = this.flat.length - 1; i >= 0; i--) {
            if (this.flat[i].moduleId === module.id) {
                this.flat.splice(i, 1);
            }
        }
        this.walk([module], [], [], 0, undefined, '');
        this.rowsMemo.clear();
    }

    /** Looks up the original PagesMenu node for a persisted favorite/pin (matched by Menu.path,
     * the same key togglePinItem/unpinFavorite compare against) so opening it can go through
     * MenuNavigationService.navigate() - i.e. the real windowed/external/report behavior -
     * instead of a favorite reimplementing that logic against its own saved Url. */
    findByPath(path: string): Menu | undefined {
        return this.flat.find(f => f.item.path === path)?.item;
    }

    /**
     * Closest PagesMenu node for an app url: an exact url match, else the node whose url is the
     * longest prefix of it. Parameterized screens (e.g.
     * `/pages/patientAccounting/billing/transactions/1596058/139791/2`) have no node of their own -
     * nothing in PagesMenu carries route params - so an ancestor's title/icon is the closest thing
     * to a real name a desktop window can put in its titlebar for them.
     */
    findEntryByUrl(url: string): { item: Menu; icon?: string } | undefined {
        const target = normalizeAppUrl(url);
        let best: FlatMenuRaw | undefined;
        let bestLength = 0;
        for (const entry of this.flat) {
            if (!entry.item.url) {
                continue;
            }
            const candidate = normalizeAppUrl(entry.item.url);
            if (candidate === target) {
                return { item: entry.item, icon: entry.icon };
            }
            if (target.startsWith(candidate + '/') && candidate.length > bestLength) {
                best = entry;
                bestLength = candidate.length;
            }
        }
        return best ? { item: best.item, icon: best.icon } : undefined;
    }

    /**
     * `moduleId` narrows the search to one rail module (the flyout's "This module" scope). It is
     * applied before `limit`, so scoping down surfaces matches the unscoped top-8 cut off, rather
     * than just deleting rows from it.
     *
     * Memoized for the same reason rowsByPaths is - see memoized(). This one matters far more:
     * matching has to happen on translated text, so a single call resolves every one of the ~309
     * flat entries (title plus each ancestor title, ~1000 translate.instant() calls) before it can
     * filter. Backing a template getter, that ran on every change-detection pass - and the root
     * AppComponent re-enters change detection from ngAfterContentChecked, so passes come in bursts
     * - which left the flyout's search box unable to keep up with typing.
     */
    searchRows(query: string, moduleId: number | null = null, limit: number = 8): FlatMenuEntry[] {
        const q = (query || '').trim().toLowerCase();
        return this.memoized(`s|${moduleId}|${limit}|${q}`, () => this.buildSearchRows(q, moduleId, limit));
    }

    /** `q` is already trimmed and lower-cased by searchRows. */
    private buildSearchRows(q: string, moduleId: number | null, limit: number): FlatMenuEntry[] {
        if (!q) {
            return [];
        }
        const resolved = this.flat
            .filter(f => moduleId === null || f.moduleId === moduleId)
            .filter(f => this.isPermitted(f))
            .map(f => this.resolve(f));
        const matches = resolved.filter(f => f.title.toLowerCase().includes(q) || f.path.toLowerCase().includes(q));
        matches.sort((a, b) => {
            const aPrefix = a.title.toLowerCase().startsWith(q) ? 0 : 1;
            const bPrefix = b.title.toLowerCase().startsWith(q) ? 0 : 1;
            return aPrefix - bPrefix;
        });
        return matches.slice(0, limit);
    }

    /**
     * Rows for the given favorite paths, in the order given - the flyout's Pinned list. A path
     * with no PagesMenu node behind it drops out: a favorite saved for a screen that has since
     * been removed, or one saved from outside PagesMenu, has no breadcrumb or module to show.
     */
    rowsByPaths(paths: string[], moduleId: number | null = null): FlatMenuEntry[] {
        return this.memoized(`p|${moduleId}|${paths.join(',')}`,
            () => this.rowsOf(paths.map(path => this.flat.find(f => f.item.path === path)), moduleId));
    }

    private rowsOf(found: (FlatMenuRaw | undefined)[], moduleId: number | null): FlatMenuEntry[] {
        return found
            .filter((raw): raw is FlatMenuRaw => !!raw && (moduleId === null || raw.moduleId === moduleId))
            .filter(raw => this.isPermitted(raw))
            .map(raw => this.resolve(raw));
    }

    private isPermitted(raw: FlatMenuRaw): boolean {
        return this.permissions.isVisible(raw.item, raw.ancestorPaths);
    }

    private resolve(raw: FlatMenuRaw): FlatMenuEntry {
        return {
            id: raw.id,
            key: raw.key,
            title: this.translate.instant(raw.titleKey),
            path: raw.ancestorTitleKeys.map(t => this.translate.instant(t)).join(' › '),
            moduleId: raw.moduleId,
            icon: raw.icon,
            item: raw.item,
        };
    }

    /**
     * searchRows and rowsByPaths both back template getters, so they run on every
     * change-detection pass for lists that only change when the query or a favorite does. Handing
     * back the same array while the inputs are unchanged keeps *ngFor from re-diffing a list that
     * has not moved, and - far more importantly for searchRows - stops the whole flat menu being
     * re-translated dozens of times a second. Rows carry resolved text, so the cache is dropped
     * whenever that resolution can change - a language switch, or a language file finishing its
     * load (see the constructor) - and whenever it outgrows its cap, since every pin/unpin and
     * every keystroke produces a cache key that is never asked for again.
     */
    private memoized(cacheKey: string, build: () => FlatMenuEntry[]): FlatMenuEntry[] {
        if (this.translate.currentLang !== this.memoLang || this.rowsMemo.size > ROWS_MEMO_CAP) {
            this.memoLang = this.translate.currentLang;
            this.rowsMemo.clear();
        }
        let rows = this.rowsMemo.get(cacheKey);
        if (!rows) {
            rows = build();
            this.rowsMemo.set(cacheKey, rows);
        }
        return rows;
    }
}
